/**
 * Confirm-then-publish host seek. Heartbeat is suppressed until the YouTube
 * player reports an actual position near the requested target.
 */
import { p2Log } from './p2Diagnostics';

export const SEEK_CONFIRM_TOLERANCE_SECONDS = 1.5;
export const SEEK_CONFIRM_TIMEOUT_MS = 10000;

export const YT_PLAYING = 1;
export const YT_PAUSED = 2;
export const YT_BUFFERING = 3;

let pending = null;
let timeoutId = null;
let nextId = 1;
let onConfirmed = null;
let getActualTime = null;

export function setHostSeekCallbacks({ onConfirmed: confirmedCb, getActualTime: timeCb } = {}) {
  onConfirmed = typeof confirmedCb === 'function' ? confirmedCb : null;
  getActualTime = typeof timeCb === 'function' ? timeCb : null;
}

export function getPendingHostSeek() {
  return pending;
}

export function isHostSeekPending() {
  return pending != null && pending.confirmed !== true;
}

export function shouldSuppressHeartbeat() {
  return isHostSeekPending();
}

export function evaluateSeekConfirmation({ ready, ytState, actualTime, pendingSeek = pending } = {}) {
  if (!pendingSeek || pendingSeek.confirmed) {
    return { confirm: false, reason: 'none' };
  }
  if (!ready) {
    return { confirm: false, reason: 'not-ready' };
  }
  if (ytState === YT_BUFFERING) {
    return { confirm: false, reason: 'buffering' };
  }
  const expectedState = pendingSeek.wantPlaying ? YT_PLAYING : YT_PAUSED;
  if (ytState !== expectedState) {
    return { confirm: false, reason: 'wrong-state' };
  }
  if (typeof actualTime !== 'number' || !Number.isFinite(actualTime)) {
    return { confirm: false, reason: 'no-time' };
  }
  if (Math.abs(actualTime - pendingSeek.targetSeconds) > SEEK_CONFIRM_TOLERANCE_SECONDS) {
    return { confirm: false, reason: 'far-from-target' };
  }
  return { confirm: true, reason: 'near-target' };
}

export function requestHostSeek({ targetSeconds, wantPlaying }) {
  if (timeoutId != null) {
    clearTimeout(timeoutId);
    timeoutId = null;
  }

  pending = {
    id: nextId,
    targetSeconds: Math.max(0, Number(targetSeconds) || 0),
    wantPlaying: Boolean(wantPlaying),
    confirmed: false,
    requestedAtMs: Date.now(),
  };
  nextId += 1;

  p2Log('[p2-host-seek]', {
    phase: 'requested',
    target: pending.targetSeconds,
    wantPlaying: pending.wantPlaying,
    id: pending.id,
  });

  timeoutId = setTimeout(() => {
    handleSeekTimeout();
  }, SEEK_CONFIRM_TIMEOUT_MS);

  return pending;
}

export function tryConfirmHostSeek({ ready, ytState, actualTime } = {}) {
  if (ytState === YT_BUFFERING && isHostSeekPending()) {
    p2Log('[p2-host-seek]', {
      phase: 'buffering',
      state: ytState,
      id: pending.id,
    });
  }

  const result = evaluateSeekConfirmation({ ready, ytState, actualTime });
  if (!result.confirm) return null;
  return confirmPending(actualTime, 'confirmed');
}

function confirmPending(actualTime, phase) {
  if (!isHostSeekPending()) return null;

  const snapshot = {
    id: pending.id,
    targetSeconds: pending.targetSeconds,
    wantPlaying: pending.wantPlaying,
    actualTime,
    phase,
  };

  if (timeoutId != null) {
    clearTimeout(timeoutId);
    timeoutId = null;
  }
  pending = null;

  p2Log('[p2-host-seek]', {
    phase,
    actual: actualTime,
    id: snapshot.id,
    wantPlaying: snapshot.wantPlaying,
  });

  onConfirmed?.(snapshot);
  return snapshot;
}

function handleSeekTimeout() {
  timeoutId = null;
  if (!isHostSeekPending()) return;

  let actual = null;
  try {
    actual = getActualTime?.();
  } catch {
    actual = null;
  }

  if (typeof actual === 'number' && Number.isFinite(actual)) {
    confirmPending(actual, 'timeout');
    return;
  }

  p2Log('[p2-host-seek]', {
    phase: 'timeout',
    actual: null,
    id: pending.id,
  });
  pending = null;
}

export function clearPendingHostSeek() {
  if (timeoutId != null) {
    clearTimeout(timeoutId);
    timeoutId = null;
  }
  pending = null;
}

export function resetHostSeekForTests() {
  clearPendingHostSeek();
  nextId = 1;
  onConfirmed = null;
  getActualTime = null;
}
